import { Learner } from './api';
import { BrandMark } from './Brand';
import { Dropdown } from './Dropdown';
import { Icon, IconName } from './Icon';
import { SupportLink } from './SupportLink';

export type NavTarget =
  | 'catalogue' | 'study-plan' | 'profile' | 'leaderboards' | 'contribute'
  | 'edit-profile' | 'admin' | 'admin-users' | 'admin-usage' | 'item-analysis';

/** The places a learner goes most. Order is how often, not alphabetical. */
const TABS: { target: NavTarget; label: string; icon: IconName }[] = [
  { target: 'catalogue', label: 'Home', icon: 'home' },
  { target: 'study-plan', label: 'Study plan', icon: 'book' },
  { target: 'profile', label: 'Your progress', icon: 'trending-up' },
  { target: 'leaderboards', label: 'Leaderboards', icon: 'trophy' },
  { target: 'contribute', label: 'Write a question', icon: 'pencil' },
];

/** Which tab a screen belongs to, so the underline stays put in sub-screens. */
const TAB_FOR: Record<string, NavTarget | undefined> = {
  catalogue: 'catalogue', configure: 'catalogue', review: 'catalogue',
  'study-plan': 'study-plan', profile: 'profile', leaderboards: 'leaderboards', contribute: 'contribute',
};

function initials(name: string) {
  const parts = name.replace(/@.*/, '').split(/[\s._-]+/).filter(Boolean);
  return ((parts[0]?.[0] ?? '?') + (parts[1]?.[0] ?? '')).toUpperCase();
}

/**
 * The site header, laid out the way people already know from the apps they use
 * daily: brand on the left, the main places as icon tabs in the centre, and
 * tools and account as round buttons on the right.
 *
 * Icon-only controls are only acceptable because each one has an accessible
 * name and a visible tooltip on hover AND keyboard focus, and the tabs show
 * their labels whenever there is room for them. On phones the same tab list
 * becomes a bottom bar — one set of markup, so the two layouts cannot drift.
 *
 * Everything is withheld during a test: navigating away would lose the
 * learner's place, and the clock is running.
 */
export function TopNav({ learner, view, inTest, isStaff, onNavigate, onSignOut }: {
  learner: Learner;
  view: string;
  inTest: boolean;
  isStaff: boolean;
  onNavigate: (target: NavTarget) => void;
  onSignOut: () => void;
}) {
  const name = learner.displayName || learner.email || 'Your account';
  const active = TAB_FOR[view];

  return (
    <header className={`topnav${inTest ? ' is-testing' : ''}`}>
      <div className="topnav__start">
        <BrandMark />
        <SupportLink disabled={inTest} />
      </div>

      <nav className="topnav__tabs" aria-label="Main">
        <ul>
          {TABS.map((t) => (
            <li key={t.target}>
              <button
                type="button"
                className="topnav__tab"
                aria-current={active === t.target ? 'page' : undefined}
                data-tip={t.label}
                disabled={inTest}
                onClick={() => onNavigate(t.target)}
              >
                <Icon name={t.icon} />
                <span className="topnav__tab-label">{t.label}</span>
              </button>
            </li>
          ))}
        </ul>
      </nav>

      <div className="topnav__end">
        {isStaff && (
          <Dropdown label="Administration tools" className="topnav__round" disabled={inTest}
                    trigger={<Icon name="grid" />}>
            <p className="dropdown__heading">Administration</p>
            <button onClick={() => onNavigate('admin')}><Icon name="shield" />Review queues</button>
            <button onClick={() => onNavigate('admin-users')}><Icon name="users" />Users</button>
            {/* Administrators only; the server refuses editors too. */}
            {learner.role === 'admin' && (
              <button onClick={() => onNavigate('admin-usage')}><Icon name="clock" />Usage</button>
            )}
            <button onClick={() => onNavigate('item-analysis')}><Icon name="bar-chart" />Question stats</button>
          </Dropdown>
        )}

        <Dropdown label={`Account: ${name}`} className="topnav__avatar" disabled={inTest}
                  trigger={<><span className="avatar" aria-hidden="true">{initials(name)}</span>
                             <span className="topnav__caret"><Icon name="chevron-down" /></span></>}>
          <div className="dropdown__who">
            <span className="avatar avatar--lg" aria-hidden="true">{initials(name)}</span>
            <span>
              <strong>{learner.displayName || 'Your account'}</strong>
              {learner.email && <span className="muted">{learner.email}</span>}
            </span>
          </div>
          <button onClick={() => onNavigate('edit-profile')}><Icon name="user" />Edit profile</button>
          <button onClick={() => onNavigate('profile')}><Icon name="trending-up" />Your progress</button>
          <button onClick={onSignOut}><Icon name="log-out" />Sign out</button>
        </Dropdown>
      </div>
    </header>
  );
}
